import { Button, Container, Grid, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import FuelAutoComplete from '@/components/fuel-autocomplete';
import { useProgress } from '@/contexts/ProgressProvider';
import { useModalContext } from '@/modals/VNModalProvider';
import type { FuelMapperTemplate } from '@/models/fuel-mapper-template';
import type { MapperTemplateForm } from '@/store/forms/Admin/MapperTemplateForm';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';

import VnModalBody from '../VnModalBody';
import VnModalFooter from '../VnModalFooter';

interface IPros {
  templateSamples: FuelMapperTemplate[];
  form: MapperTemplateForm;
  onSave: (form: MapperTemplateForm) => void;
  onTestMapper: (form: MapperTemplateForm) => void;
  isExisting: boolean;
}

const Left = styled.div`
  width: 50%;
  border-right: 2px solid gray;
  border-style: dotted;
`;
const Right = styled.div`
  width: 50%;
  margin-left: 10px;
  margin-bottom: 10px;
`;

const MapperTemplateModal = ({ templateSamples, form, onSave, isExisting, onTestMapper }: IPros) => {
  const [selectedSampleTemplate, setSelectedTemplate] = useState<string>(
    templateSamples?.length > 0 ? templateSamples[0].template : ''
  );
  const [sampleTemplate, setSampleTemplate] = useState(templateSamples?.length > 0 ? templateSamples[0] : '');
  const [selectedContentType, setSelectedContenType] = useState<any>(form.selectedContentType);
  const { alert } = useAppUtils();
  const [testMapperButtonDisabled, setTestMapperButtonDisabled] = useState(true);
  const { loading } = useProgress();
  const { onClose } = useModalContext();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<MapperTemplateForm>({
    defaultValues: useMemo(() => {
      return form;
    }, [form]),
  });

  const handleClose = () => {
    // setOpen(false);
    onClose();
  };
  const submit: SubmitHandler<MapperTemplateForm> = async (data: MapperTemplateForm) => {
    // Todo Add validation -
    if (selectedContentType) {
      setValue('contentType', selectedContentType.text);
      setValue('inputFormat', selectedContentType.inputFormat);
      setValue('outputFormat', selectedContentType.outputFormat);
    }
    // console.log('Ou Submitted', getValues());
    onSave(getValues());
    // onClose();
  };
  const testMapper: SubmitHandler<MapperTemplateForm> = async (data: MapperTemplateForm) => {
    // Todo Add validation -
    if (selectedContentType) {
      setValue('contentType', selectedContentType.text);
      setValue('inputFormat', selectedContentType.inputFormat);
      setValue('outputFormat', selectedContentType.outputFormat);
    }
    onTestMapper(getValues());
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value.template);
  };
  const handleContentTypeChange = (value) => {
    setSelectedContenType(value);
    setValue('contentType', value.text);
    if (templateSamples.length > 0) {
      const sample = templateSamples.find((item) => item.inputFormat.toLowerCase() === value.inputFormat.toLowerCase());
      setSelectedTemplate(sample?.template);
      setSampleTemplate(sample);
    }
  };

  const getTitle = () => {
    return isExisting ? 'Update Template' : 'Create Template';
  };

  const copySampleTemplate = async () => {
    // console.log('selectedSampleTemplate copied', selectedSampleTemplate);
    clientUtils.copyToClipboard(selectedSampleTemplate);
  };
  const onDataSourceUrlBlur = () => {
    if (getValues('dataSourceUrl') === null || getValues('dataSourceUrl').length === 0) {
      setTestMapperButtonDisabled(true);
    } else {
      setTestMapperButtonDisabled(false);
    }
  };

  useEffect(() => {
    onDataSourceUrlBlur();
  }, [getValues('dataSourceUrl')]);

  return (
    <>
      <VnModalBody>
        <Container section="component" maxWidth="lg">
          <Grid className="no-padding section-box" container spacing={2}>
            <Grid item xs={6} sx={{ borderRight: '2px solid gray', borderStyle: 'dotted' }}>
              <Grid className="no-padding section-box" container spacing={2}>
                {isExisting && (
                  <Grid item xs={11}>
                    <TextField variant="outlined" label="Template ID" fullWidth size="small" disabled value={form.id} />
                  </Grid>
                )}
                <Grid item xs={11}>
                  <TextField
                    variant="outlined"
                    label="Template Name"
                    fullWidth
                    size="small"
                    error={!!errors.templateName}
                    required
                    placeholder="Enter Template Name"
                    {...register('templateName', { required: true })}
                  />
                  <Typography variant="inherit" className="text-danger">
                    {errors.templateName && <span>Template Name is required</span>}
                  </Typography>
                </Grid>
                <Grid item xs={11}>
                  <TextField
                    variant="outlined"
                    label="Data Source Url"
                    fullWidth
                    size="small"
                    placeholder="Enter Data Source Url"
                    {...register('dataSourceUrl')}
                    onBlur={onDataSourceUrlBlur}
                  />
                  {/* <Typography variant="inherit" className="text-danger">
                      {errors.dataSourceUrl && <span>Data Source Url is required</span>}
                    </Typography> */}
                </Grid>
                <Grid item xs={11}>
                  <FuelAutoComplete
                    id="template-sample-select"
                    items={form.contentTypes ?? []}
                    itemText="name"
                    itemKey="id"
                    labelText="Content Type *"
                    placeHolderText="Select Content Type"
                    controlWidth={505}
                    multiSelect={false}
                    labelPaddingTop="-5px"
                    defaultValue={selectedContentType ?? ''}
                    isRequired
                    hasError={!!errors.template}
                    {...register('contentType', { required: true })}
                    onChange={handleContentTypeChange}
                  />
                  <Typography variant="inherit" className="text-danger">
                    {errors.contentType && <span>Content Type is required</span>}
                  </Typography>
                </Grid>
                <Grid item xs={11}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Template"
                    placeholder="Add Template"
                    multiline
                    minRows={8}
                    {...register('template', { required: true })}
                    error={!!errors.template}
                    required
                  />
                  <Typography variant="inherit" className="text-danger">
                    {errors.template && <span>Template is required</span>}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid className="right-grid" container spacing={2}>
                <Grid item xs={11}>
                  <FuelAutoComplete
                    id="template-sample-select"
                    items={templateSamples}
                    itemText="templateName"
                    itemKey="id"
                    labelText="Sample Template"
                    placeHolderText="Select Sample Template"
                    controlWidth={510}
                    multiSelect={false}
                    labelPaddingTop="-5px"
                    onChange={handleTemplateChange}
                    defaultValue={sampleTemplate}
                  />
                </Grid>
                <Grid item xs={11}>
                  <TextField
                    variant="outlined"
                    value={selectedSampleTemplate}
                    fullWidth
                    placeholder="Sample Template"
                    multiline
                    minRows={8}
                    maxRows={12}
                    disabled
                  />
                </Grid>
                <Grid item xs={11}>
                  <Button autoFocus color="primary" variant="contained" sx={{ marginRight: 1 }} onClick={copySampleTemplate}>
                    Copy Sample Template
                  </Button>
                  <Button
                    autoFocus
                    color="primary"
                    variant="contained"
                    disabled={testMapperButtonDisabled}
                    onClick={handleSubmit(testMapper)}
                  >
                    Test Mapper
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            {' '}
            <Button color="primary" variant="contained" disabled={loading} autoFocus onClick={handleSubmit(submit)}>
              {isExisting ? 'Update' : 'Create'}
            </Button>
            <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
              Cancel
            </Button>
          </>
        }
      />
    </>
  );
};

export default MapperTemplateModal;
